import { useEffect, useRef, useState } from "react";
import { FaExclamationTriangle, FaInfoCircle, FaCheckCircle, FaTimes } from "react-icons/fa";
import { commonModalSetting } from "JS/common";

const CommonModal = ({ setModal, modal, okFn }) => {
  const contentRef = useRef(null);
  const [mouseDownOnOverlay, setMouseDownOnOverlay] = useState(false);

  // 스타일 정의
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      animation: 'fadeIn 0.2s ease-out'
    },
    modal: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      border: '1px solid #e2e8f0',
      minWidth: '400px',
      maxWidth: '500px',
      maxHeight: '80vh',
      overflow: 'hidden',
      animation: 'slideIn 0.2s ease-out'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 24px 16px 24px',
      borderBottom: '1px solid #e2e8f0'
    },
    icon: {
      fontSize: '48px',
      marginBottom: '8px'
    },
    alertIcon: {
      color: '#f59e0b'
    },
    confirmIcon: {
      color: '#dc2626'
    },
    successIcon: {
      color: '#10b981'
    },
    infoIcon: {
      color: '#3b82f6'
    },
    content: {
      padding: '20px 24px',
      fontSize: '16px',
      lineHeight: '1.6',
      color: '#374151',
      textAlign: 'center',
      wordBreak: 'keep-all'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      padding: '16px 24px 24px 24px',
      justifyContent: 'center'
    },
    confirmButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      minWidth: '80px',
      justifyContent: 'center'
    },
    confirmButtonDanger: {
      backgroundColor: '#dc2626'
    },
    cancelButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: '#ffffff',
      color: '#6b7280',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      minWidth: '80px',
      justifyContent: 'center'
    }
  };

  // 모달 타입에 따른 아이콘 반환
  const getModalIcon = () => {
    const iconStyle = styles.icon;
    
    if (modal.mode === "confirm") {
      return (
        <FaExclamationTriangle 
          style={{...iconStyle, ...styles.confirmIcon}} 
        />
      );
    } else if (modal.context?.includes("성공") || modal.context?.includes("완료")) {
      return (
        <FaCheckCircle 
          style={{...iconStyle, ...styles.successIcon}} 
        />
      );
    } else if (modal.context?.includes("정보") || modal.context?.includes("알림")) {
      return (
        <FaInfoCircle 
          style={{...iconStyle, ...styles.infoIcon}} 
        />
      );
    } else {
      return (
        <FaExclamationTriangle 
          style={{...iconStyle, ...styles.alertIcon}} 
        />
      );
    }
  };

  // 확인 버튼 스타일 결정
  const getConfirmButtonStyle = () => {
    if (modal.mode === "confirm") {
      return { ...styles.confirmButton, ...styles.confirmButtonDanger };
    }
    return styles.confirmButton;
  };

  useEffect(() => {
    if (modal.context === undefined || modal.context === "undefined") {
      setModal(false);
      return;
    }

    // innerHTML 대신 textContent 사용하여 XSS 방지
    if (contentRef.current) {
      // HTML 태그가 포함된 경우 처리
      if (modal.context.includes('<br/>') || modal.context.includes('<br>')) {
        contentRef.current.innerHTML = new DOMParser().parseFromString(
          modal.context,
          "text/html"
        ).body.innerHTML;
      } else {
        contentRef.current.textContent = modal.context;
      }
    }

    // 키보드 이벤트 처리
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        if (modal.mode === "confirm") {
          okFn();
          commonModalSetting(setModal, false);
        } else {
          commonModalSetting(setModal, false);
        }
      } else if (e.key === "Escape") {
        commonModalSetting(setModal, false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // 클린업
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [modal.context, modal.mode, okFn, setModal]);

  // 오버레이에서 mousedown 시작 여부 추적
  const handleOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) {
      setMouseDownOnOverlay(true);
    }
  };

  // mousedown과 mouseup이 모두 overlay에서 발생했을 때만 모달 닫기
  const handleOverlayMouseUp = (e) => {
    if (e.target === e.currentTarget && mouseDownOnOverlay) {
      commonModalSetting(setModal, false);
    }
    setMouseDownOnOverlay(false);
  };

  return (
    <>
      <div
        style={styles.overlay}
        onMouseDown={handleOverlayMouseDown}
        onMouseUp={handleOverlayMouseUp}
      >
        <div style={styles.modal}>
          {/* 헤더 영역 */}
          <div style={styles.header}>
            {getModalIcon()}
          </div>

          {/* 콘텐츠 영역 */}
          <div style={styles.content} ref={contentRef}>
            {/* 내용이 여기에 동적으로 삽입됩니다 */}
          </div>

          {/* 버튼 영역 */}
          <div style={styles.buttonGroup}>
            <button
              style={getConfirmButtonStyle()}
              onClick={() => {
                okFn();
                commonModalSetting(setModal, false);
              }}
              onMouseEnter={(e) => {
                if (modal.mode === "confirm") {
                  e.target.style.backgroundColor = '#b91c1c';
                } else {
                  e.target.style.backgroundColor = '#2563eb';
                }
              }}
              onMouseLeave={(e) => {
                if (modal.mode === "confirm") {
                  e.target.style.backgroundColor = '#dc2626';
                } else {
                  e.target.style.backgroundColor = '#3b82f6';
                }
              }}
            >
              <FaCheckCircle size={14} />
              확인
            </button>
            
            {modal.mode === "confirm" && (
              <button
                style={styles.cancelButton}
                onClick={() => {
                  commonModalSetting(setModal, false);
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.borderColor = '#9ca3af';
                  e.target.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.color = '#6b7280';
                }}
              >
                <FaTimes size={14} />
                취소
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 애니메이션 스타일 */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @media (max-width: 480px) {
          .modal {
            min-width: 300px !important;
            margin: 20px !important;
          }
        }
      `}</style>
    </>
  );
};

export default CommonModal;